const TRUE_FLAG_TOKENS = new Set(["true", "1", "yes"]);
const GENERATED_STATUS_TOKENS = new Set([
  "po_generated",
  "purchase_order_generated",
  "generated",
]);
const DISMISSED_STATUS_TOKENS = new Set(["dismissed", "rejected", "declined"]);

const BID_ID_FIELDS = [
  "rfq_vendor_quote_id",
  "parts_rfq_bids_id",
  "parts_rfq_bid_id",
  "bid_id",
  "vendor_quote_id",
  "quote_id",
  "id",
];
const STATUS_FIELDS = ["bid_status", "status", "po_status", "rfq_bid_status", "quote_status"];
const GENERATED_FLAG_FIELDS = [
  "po_generated",
  "is_po_generated",
  "purchase_order_generated",
  "po_created",
  "is_po_created",
];
const PURCHASE_ORDER_ID_FIELDS = ["purchase_order_id", "po_id", "parts_purchase_order_id"];
const CONTAINER_GENERATED_ID_FIELDS = [
  "rfq_vendor_quote_id",
  "parts_rfq_bids_id",
  "parts_rfq_bid_id",
  "bid_id",
  "rfq_bid_id",
  "vendor_quote_id",
  "quote_id",
  "selected_bid_id",
  "generated_bid_id",
  "po_generated_bid_id",
];
const VENDOR_ID_FIELDS = ["vendor_id", "part_vendor_id"];

const toStatusToken = (value) =>
  String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");

export const toIdKey = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const hasTruthyFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return TRUE_FLAG_TOKENS.has(toStatusToken(value));
};

const getFirstPresentId = (source, fields) => {
  if (!source) return null;
  for (const field of fields) {
    const value = toIdKey(source[field]);
    if (value) return value;
  }
  return null;
};

const getAllPresentIds = (source, fields) => {
  if (!source) return [];
  const ids = [];
  for (const field of fields) {
    const value = toIdKey(source[field]);
    if (value) ids.push(value);
  }
  return Array.from(new Set(ids));
};

const getBidStatusToken = (bid) => {
  for (const field of STATUS_FIELDS) {
    const token = toStatusToken(bid?.[field]);
    if (token) return token;
  }
  return "";
};

export const getBidIdentifier = (bid) => getFirstPresentId(bid, BID_ID_FIELDS);
const getBidIdentifierCandidates = (bid) => getAllPresentIds(bid, BID_ID_FIELDS);

const getBidVendorIdentifier = (bid) =>
  getFirstPresentId(bid, VENDOR_ID_FIELDS) || getFirstPresentId(bid?.vendor, [...VENDOR_ID_FIELDS, "id"]);

const toComparableToken = (value) => String(value ?? "").toLowerCase().trim();
const toNumericComparableToken = (value) => {
  const token = toComparableToken(value);
  const numericPart = token.replace(/[^\d]/g, "");
  return numericPart || null;
};

const isSameIdentifier = (left, right) => {
  const normalizedLeft = toIdKey(left);
  const normalizedRight = toIdKey(right);
  if (!normalizedLeft || !normalizedRight) return false;

  if (toComparableToken(normalizedLeft) === toComparableToken(normalizedRight)) {
    return true;
  }

  const leftNumeric = toNumericComparableToken(normalizedLeft);
  const rightNumeric = toNumericComparableToken(normalizedRight);
  return Boolean(leftNumeric && rightNumeric && leftNumeric === rightNumeric);
};

const isBidPoGenerated = (bid) => {
  if (!bid) return false;

  if (GENERATED_FLAG_FIELDS.some((field) => hasTruthyFlag(bid[field]))) {
    return true;
  }

  if (PURCHASE_ORDER_ID_FIELDS.some((field) => toIdKey(bid[field]))) {
    return true;
  }

  return GENERATED_STATUS_TOKENS.has(getBidStatusToken(bid));
};

const isBidDismissed = (bid) => DISMISSED_STATUS_TOKENS.has(getBidStatusToken(bid));

const getGeneratedBidIdFromBids = (bids = []) => {
  for (const bid of bids) {
    if (isBidPoGenerated(bid)) {
      const bidId = getBidIdentifierCandidates(bid)[0] || null;
      if (bidId) return bidId;
    }
  }
  return null;
};

const getGeneratedVendorIdFromValue = (value) => {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const nestedArrayVendorId = getGeneratedVendorIdFromValue(item);
      if (nestedArrayVendorId) return nestedArrayVendorId;
    }
    return null;
  }

  if (typeof value !== "object") return null;

  const directVendorId =
    getFirstPresentId(value, VENDOR_ID_FIELDS) ||
    getFirstPresentId(value?.vendor, [...VENDOR_ID_FIELDS, "id"]);
  if (directVendorId) return directVendorId;

  const nestedSources = [
    value?.purchase_order,
    value?.po,
    value?.purchaseOrder,
    value?.vendor_quote,
    value?.rfq_vendor_quote,
    value?.quote,
    value?.selected_bid,
    value?.selected_quote,
    value?.data,
  ];

  for (const source of nestedSources) {
    const nestedObjectVendorId = getGeneratedVendorIdFromValue(source);
    if (nestedObjectVendorId) return nestedObjectVendorId;
  }

  return null;
};

const getGeneratedBidIdFromValue = (value, allowLooseId = false) => {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const nestedArrayId = getGeneratedBidIdFromValue(item, true);
      if (nestedArrayId) return nestedArrayId;
    }
    return null;
  }

  if (typeof value === "string" || typeof value === "number") {
    return toIdKey(value);
  }

  if (typeof value !== "object") return null;

  const directId = getFirstPresentId(
    value,
    allowLooseId ? [...CONTAINER_GENERATED_ID_FIELDS, "id"] : CONTAINER_GENERATED_ID_FIELDS
  );
  if (directId) return directId;

  const nestedSources = [
    value?.bid,
    value?.quote,
    value?.vendor_quote,
    value?.rfq_vendor_quote,
    value?.generated_bid,
    value?.selected_bid,
    value?.data,
  ];

  for (const source of nestedSources) {
    const nestedObjectId = getGeneratedBidIdFromValue(source, true);
    if (nestedObjectId) return nestedObjectId;
  }

  return null;
};

const getGeneratedBidIdFromContainer = (container) => {
  const directId = getFirstPresentId(container, CONTAINER_GENERATED_ID_FIELDS);
  if (directId) return directId;

  const nestedSources = [
    container?.po_generated,
    container?.purchase_order,
    container?.po,
    container?.purchaseOrder,
    container?.latest_purchase_order,
  ];

  for (const source of nestedSources) {
    const nestedId = getGeneratedBidIdFromValue(source, true);
    if (nestedId) return nestedId;
  }

  return null;
};

const hasRfqGeneratedPo = (rfqPoGenerated) => {
  if (
    hasTruthyFlag(rfqPoGenerated) ||
    GENERATED_STATUS_TOKENS.has(toStatusToken(rfqPoGenerated))
  ) {
    return true;
  }

  if (typeof rfqPoGenerated === "string" || typeof rfqPoGenerated === "number") {
    return Boolean(toIdKey(rfqPoGenerated));
  }

  if (Array.isArray(rfqPoGenerated)) {
    return rfqPoGenerated.length > 0;
  }

  if (rfqPoGenerated && typeof rfqPoGenerated === "object") {
    return (
      Boolean(getGeneratedBidIdFromValue(rfqPoGenerated, true)) ||
      Object.keys(rfqPoGenerated).length > 0
    );
  }

  return false;
};

export const resolveBidPoActionState = ({
  bid,
  bids = [],
  rfqPoGenerated = false,
  generatedBidId = null,
  container = null,
}) => {
  const bidIdCandidates = getBidIdentifierCandidates(bid);
  const bidVendorId = getBidVendorIdentifier(bid);
  const knownGeneratedBidId =
    toIdKey(generatedBidId) ||
    getGeneratedBidIdFromValue(rfqPoGenerated, true) ||
    getGeneratedBidIdFromBids(bids) ||
    getGeneratedBidIdFromContainer(container);
  const knownGeneratedVendorId =
    getGeneratedVendorIdFromValue(rfqPoGenerated) ||
    getGeneratedVendorIdFromValue(container?.po_generated) ||
    getGeneratedVendorIdFromValue(container?.purchase_order) ||
    getGeneratedVendorIdFromValue(container?.po) ||
    getGeneratedVendorIdFromValue(container?.purchaseOrder) ||
    getGeneratedVendorIdFromValue(container?.latest_purchase_order);

  const generatedForThisBid =
    isBidPoGenerated(bid) ||
    (knownGeneratedBidId &&
      bidIdCandidates.some((candidateId) => isSameIdentifier(candidateId, knownGeneratedBidId))) ||
    (bidVendorId && knownGeneratedVendorId && isSameIdentifier(bidVendorId, knownGeneratedVendorId));
  const dismissedForThisBid = isBidDismissed(bid);
  const rfqIsLocked = hasRfqGeneratedPo(rfqPoGenerated) || Boolean(knownGeneratedBidId);

  if (generatedForThisBid) {
    return { label: "PO Generated", disabled: true, intent: "generated" };
  }

  if (dismissedForThisBid || rfqIsLocked) {
    return { label: "Dismissed", disabled: true, intent: "dismissed" };
  }

  return { label: "Generate PO", disabled: false, intent: "action" };
};
